import api, { ApiExtraOptions } from '.';
import {
  ActivityLogItem,
  Caseload,
  CaseloadWithCases,
  CaseWithDetails,
  AddCaseRequest,
} from '@karehero/models';

export const careManagementApi = api.injectEndpoints({
  endpoints: (builder) => ({
    createAccountCaseload: builder.mutation<Caseload, { accountId: string }>({
      query: ({ accountId }) => ({
        url: `/account/${accountId}/caseload`,
        method: 'POST',
      }),
      invalidatesTags: ['Caseload', 'Accounts'],
    }),

    getAccountCaseload: builder.query<Caseload, { accountId: string }>({
      query: ({ accountId }) => `/account/${accountId}/caseload`,
      providesTags: ['Caseload'],
      // 404: the account has no caseload yet.
      extraOptions: { expectedStatuses: ['404'] } satisfies ApiExtraOptions,
    }),

    getCaseload: builder.query<CaseloadWithCases, { caseloadId: string }>({
      query: ({ caseloadId }) => `/caseload/${caseloadId}`,
      providesTags: ['Caseload'],
    }),

    addCase: builder.mutation<
      CaseWithDetails,
      { caseloadId: string; body: AddCaseRequest }
    >({
      query: ({ caseloadId, body }) => ({
        url: `/caseload/${caseloadId}/cases`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Caseload', 'CareCircles'],
    }),

    removeCase: builder.mutation<void, { caseloadId: string; caseId: string }>({
      query: ({ caseloadId, caseId }) => ({
        url: `/caseload/${caseloadId}/cases/${caseId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Caseload', 'CareCircles'],
    }),

    makeCasePrimary: builder.mutation<
      void,
      { careCircleId: string; caseId: string }
    >({
      query: ({ careCircleId, caseId }) => ({
        url: `/care-circle/${careCircleId}/cases/${caseId}/make-primary`,
        method: 'POST',
      }),
      invalidatesTags: ['Caseload', 'CareCircles'],
    }),

    getCaseloadActivity: builder.query<
      ActivityLogItem[],
      { caseloadId: string; limit?: number; offset?: number }
    >({
      query: ({ caseloadId, limit = 20, offset = 0 }) => ({
        url: `/caseload/${caseloadId}/activity`,
        params: { limit, offset },
      }),
    }),
  }),
});

export const {
  useCreateAccountCaseloadMutation,
  useGetAccountCaseloadQuery,
  useGetCaseloadQuery,
  useAddCaseMutation,
  useRemoveCaseMutation,
  useMakeCasePrimaryMutation,
  useGetCaseloadActivityQuery,
} = careManagementApi;
